/** Focus background pipeline — the timer must outlive the tab and land a
 *  system notification in the phone's notification bar when the time is up.
 *
 *  Mechanism: while a session runs, a deadline (endsAtMs) is persisted to
 *  localStorage. Two independent delivery paths fire when it passes:
 *  1. IN-APP (app still open): a wall-clock interval checks the deadline —
 *     immune to background tab throttling that slows setInterval.
 *  2. ON RETURN (app was closed): the provider bootstraps, sees a missed
 *     deadline, saves the session server-side (which fires the VAPID push
 *     → the phone's notification bar even though the app was closed).
 *  Pure helpers are unit-tested. */

export const FOCUS_ENDS_KEY = "nelurio.focus.endsAt.v1";
export const FOCUS_NOTIFIED_KEY = "nelurio.focus.notified.v1";

export interface FocusEndRecord { endsAtMs: number; durationMinutes: number; goal: string; courseId: string | null; treeKind: string }

/** Persist the running session's deadline (called on start/resume/pause). */
export function storeFocusEnd(record: FocusEndRecord | null, store: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null = typeof window === "undefined" ? null : window.localStorage): void {
  try {
    if (record) store?.setItem(FOCUS_ENDS_KEY, JSON.stringify(record));
    else store?.removeItem(FOCUS_ENDS_KEY);
  } catch { /* private mode */ }
}

export function readFocusEnd(store: Pick<Storage, "getItem"> | null = typeof window === "undefined" ? null : window.localStorage): FocusEndRecord | null {
  try {
    const raw = store?.getItem(FOCUS_ENDS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FocusEndRecord;
    if (parsed && typeof parsed.endsAtMs === "number" && typeof parsed.durationMinutes === "number") return parsed;
    return null;
  } catch {
    return null;
  }
}

/** Wall-clock remaining ms until the deadline (drift-proof: it never depends
 *  on interval ticks — closed-tab time counts automatically). */
export function remainingUntilDeadline(record: FocusEndRecord, now: number): number {
  return Math.max(0, record.endsAtMs - now);
}

/** Did the deadline pass without a notification yet? (missed-while-closed) */
export function deadlineMissed(record: FocusEndRecord, notified: boolean, now: number): boolean {
  return record.endsAtMs < now && !notified;
}

/** One-shot notification marker so a deadline never double-fires. */
export function isFocusNotified(store: Pick<Storage, "getItem"> | null = typeof window === "undefined" ? null : window.localStorage): boolean {
  try { return store?.getItem(FOCUS_NOTIFIED_KEY) === "1"; } catch { return false; }
}

export function markFocusNotified(store: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null = typeof window === "undefined" ? null : window.localStorage): void {
  try { store?.setItem(FOCUS_NOTIFIED_KEY, "1"); } catch { /* ignore */ }
}

export function clearFocusNotified(store: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null = typeof window === "undefined" ? null : window.localStorage): void {
  try { store?.removeItem(FOCUS_NOTIFIED_KEY); } catch { /* ignore */ }
}
